import React, { useEffect, useState, useMemo, useRef } from 'react';
import Header from './components/Header/Header';
import Settings from './components/Settings/Settings';
import WelcomeBlock from './components/WelcomeBlock/WelcomeBlock';
import Premium from './components/Premium/Premium';
import Referral from './components/Referral/Referral';

import Assistant from './components/Assistant/Assistant';
import { useAssistantHints } from './hooks/useAssistantHints';
import styles from './App.module.css';
import { useAuth } from './context/AuthContext';
import { fetchData } from './api/api'; 
import { AudioRecorderProvider } from './context/AudioRecorderContext';
import { AudioTelegramRecorderProvider } from './context/AudioTelegramRecorderProvider';
import { AndroidAudioRecorderProvider } from './context/AndroidAudioRecorderProvider';
import { AssistantProvider } from './components/Assistant/AssistantContext';
import { AppConfigProvider } from './context/AppConfigContext';

import { getAppBaseConfig, mergeAppConfig } from './utils/appConfig';
import { detectDeviceLanguage } from './utils/languageDetection';

import { ErrorProvider } from './context/errors/ErrorProvider';
import GlobalErrorBanner from './context/errors/GlobalErrorBanner';
import ErrorBoundary from './context/errors/ErrorBoundary';

import DialogList from './modules/Dialogs/DialogList';
import DialogScreen from './modules/Dialogs/DialogScreen';
import LessonList from './modules/Lessons/LessonList';
import LessonScreen from './modules/Lessons/LessonScreen';
import ExamList from './modules/Exam/ExamList';
import ExamScreen from './modules/Exam/ExamScreen';
import Game from './modules/Game/Game';
import PathHome from './modules/Path/PathHome';
import PathLessons from './modules/Path/PathLessons';
import PathLearn from './modules/Path/PathLearn';
import PathPractice from './modules/Path/PathPractice';
import PathDialogs from './modules/Path/PathDialogs';
import PathExams from './modules/Path/PathExams';
import PathCheckGame from './modules/Path/check/PathCheckGame';

import ShowVip from './components/ShowVip';
import CharityButtons from './components/CharityButtons/CharityButtons';
import Logo from './components/Logo/Logo';
import RegistrationButtons from './components/RegistrationButtons/RegistrationButtons';
import FlyingPoints from './components/FlyingPoints/FlyingPoints';
import FlyingPhrases from './components/FlyingPhrases/FlyingPhrases';
import AccessDenied from './components/AccessDenied/AccessDenied';

import deFlagImg from './assets/images/flags/de2.png';
import enFlagImg from './assets/images/flags/nen.png';
import ruFlagImg from './assets/images/flags/ru1.png';
import ukFlagImg from './assets/images/flags/uk.png';

import { Capacitor } from '@capacitor/core';
import { useAppConfig } from './context/AppConfigContext'; 
import { requestMicrophonePermission, requestPushPermission } from './utils/permissions';

import PromoModal from './components/PromoModal';
import PwaInfoModal from './components/PwaInfoModal';
import { SITE_CONFIG } from './config/siteConfig';
//import Test from './Test';

const LEGACY_HOME_REGISTRATION_CUTOFF_TS = Date.parse('2026-03-15T00:00:00Z');
const PWA_INFO_MIN_ACCOUNT_AGE_MS = 2 * 24 * 60 * 60 * 1000;
const PWA_INFO_TEST_FLAG_QUERY = 'pwaInfoTest';
const PWA_INFO_TEST_FLAG_STORAGE = 'pwaInfoTest';
const PWA_INFO_SEEN_STORAGE_PREFIX = 'pwaInfoSeen';
const PWA_INFO_FORCE_SHOW = false;

const getRegistrationTimestamp = (userInfo) => {
  const rawDate =
    userInfo?.createdAt ||
    userInfo?.created_at ||
    userInfo?.registrationDate ||
    userInfo?.registeredAt ||
    userInfo?.registered_at ||
    null;

  if (!rawDate) return null;
  const ts = new Date(rawDate).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const getIsPwaInstalled = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') {
    return Boolean(window.navigator.standalone);
  }

  const mediaStandalone = window.matchMedia('(display-mode: standalone)');
  const mediaFullscreen = window.matchMedia('(display-mode: fullscreen)');
  const mediaMinimalUi = window.matchMedia('(display-mode: minimal-ui)');
  const iOSStandalone = Boolean(window.navigator.standalone);

  return iOSStandalone || mediaStandalone.matches || mediaFullscreen.matches || mediaMinimalUi.matches;
};

const getIsPwaInfoTestEnabled = () => {
  if (PWA_INFO_FORCE_SHOW) return true;
  if (typeof window === 'undefined') return false;

  const queryValue = new URLSearchParams(window.location.search).get(PWA_INFO_TEST_FLAG_QUERY);
  if (queryValue === '1' || queryValue === 'true') return true;
  if (queryValue === '0' || queryValue === 'false') return false;

  try {
    const storageValue = window.localStorage.getItem(PWA_INFO_TEST_FLAG_STORAGE);
    return storageValue === '1' || storageValue === 'true';
  } catch (_) {
    return false;
  }
};

const getPwaInfoSeenStorageKey = (userId) => `${PWA_INFO_SEEN_STORAGE_PREFIX}:${String(userId || 'anon')}`;


const App = () => {
	
  const { userId, isGuest:temp, userInfo, platform, isLoading, isAndroidApp, deviceId, deviceLanguage, deviceArch, deviceMemory } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative;
  const vipLevel = userInfo?.vip || 0;
  const registrationTimestamp = getRegistrationTimestamp(userInfo);
  const canUseLegacyHomeMode =
    Number(vipLevel) >= 6 &&
    Number.isFinite(registrationTimestamp) &&
    registrationTimestamp < LEGACY_HOME_REGISTRATION_CUTOFF_TS;
	
  // Определяем язык устройства сразу
  const deviceDetectedLang = useMemo(() => 
    detectDeviceLanguage(isAndroidApp, deviceLanguage), 
    [isAndroidApp, deviceLanguage]
  );

  // Состояния языков
  const [language, setLanguage] = useState(deviceDetectedLang); // Начинаем с языка устройства
  const [flagCode, setFlagCode] = useState('GB');
  const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState(null);
  const [isServerLanguageSet, setIsServerLanguageSet] = useState(false); // Флаг получения с сервера
  const [serverSettings, setServerSettings] = useState(false);
  
  // Создаем baseConfig
  const baseConfig = useMemo(() => 
    getAppBaseConfig(platform, { isAndroidApp, deviceId, deviceLanguage, deviceArch, deviceMemory }), 
    [platform, isAndroidApp, deviceId, deviceLanguage]
  );

  const [config, setConfig] = useState(() => mergeAppConfig(baseConfig, deviceDetectedLang));
  const [isPwaInfoOpen, setIsPwaInfoOpen] = useState(false);
  const [isInstalledPwa, setIsInstalledPwa] = useState(false);
  const [isPwaInfoTestEnabled, setIsPwaInfoTestEnabled] = useState(false);
  const [isPwaInfoSeen, setIsPwaInfoSeen] = useState(false);
  
  // Очки и статистика
  const [points, setPoints] = useState(0);
  const [pointsDelta, setPointsDelta] = useState(null);
  const [phrasesCount, setPhrasesCount] = useState(0);
  const [learnedWordsCount, setLearnedWordsCount] = useState(0);
  const [phrasesDelta, setPhrasesDelta] = useState(null);
  const [audioTime, setAudioTime] = useState(100); // умножается от VIP   
  
  const prevPhrasesCount = useRef(phrasesCount);
  const [gameLevel, setGameLevel] = useState('A1');
  
	const loadLanguage = async () => {

	  try {
		// Первый вызов — всегда просим настройки
		const result = await fetchData('/api/settings', 'POST', {
		  userId,
		  askSetting: true, // всегда спрашиваем, но действуем в зависимости от наличия targetLang
		  askPoints: true,
		});

		const settings = result.settings || {};
		const stats = result.stats || {};
		const serverGameLevel = result.settings.gameLevel || 'A1';
		setGameLevel(serverGameLevel)
		
		const serverBaseLang = settings.baseLanguage || deviceDetectedLang;
		const targetLang = settings.targetLanguage || null;
		const level = settings.level || 'A2'; // уровень в самом приложении. сейчас уже не надо!

		// Обновляем язык, если он пришел с сервера и отличается
		if (settings.baseLanguage && settings.baseLanguage !== deviceDetectedLang) {
		  setLanguage(settings.baseLanguage);
		}

		// Если targetLang отсутствует — можно инициировать UI запрос (например, показать диалог)
		if (!targetLang) {
		  setIsServerLanguageSet(false);
		  return; // Можно здесь остановить
		}

		setTargetLanguage(targetLang);
		setIsServerLanguageSet(true);

		setFlagCode(targetLang.toUpperCase() === 'EN' ? 'GB' : targetLang.toUpperCase());

		const merged = mergeAppConfig(baseConfig, serverBaseLang, targetLang, level, userInfo, serverGameLevel);
		setConfig(merged);

		setServerSettings(true);

		// Статистика
		setPoints(stats.total_points || 0);
		setPhrasesCount(stats.phrases_count || 0);
        setAudioTime(stats.audio_time_seconds || 0);
        setLearnedWordsCount(stats.learned_words_count || 0);

	  } catch (error) {
		console.error('❌ Error loading language:', error);
		setConfig(mergeAppConfig(baseConfig, deviceDetectedLang, null, 'A2'));
		setIsServerLanguageSet(true);
	  } finally {
		setIsLanguageLoaded(true);
	  }
	};

	const handleWelcomeSaved = ({ baseLanguage, targetLanguage }) => {
	  setLanguage(baseLanguage);
	  setTargetLanguage(targetLanguage);
	  setIsServerLanguageSet(true);

	  setFlagCode(
		targetLanguage?.toUpperCase() === 'EN'
		  ? 'GB'
		  : targetLanguage?.toUpperCase()
	  );

	  const merged = mergeAppConfig(
		baseConfig,
		baseLanguage,
		targetLanguage,
		'A2',
		userInfo,
		gameLevel
	  );
	  setConfig(merged);
	};



  // Эффект для загрузки языка при готовности userId
  useEffect(() => {
    if (userId && !isLoading) {
      loadLanguage();
    }
  }, [userId, isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (typeof window.matchMedia !== 'function') {
      setIsInstalledPwa(getIsPwaInstalled());
      setIsPwaInfoTestEnabled(getIsPwaInfoTestEnabled());
      return undefined;
    }

    const mediaStandalone = window.matchMedia('(display-mode: standalone)');
    const mediaFullscreen = window.matchMedia('(display-mode: fullscreen)');
    const mediaMinimalUi = window.matchMedia('(display-mode: minimal-ui)');

    const updatePwaFlags = () => {
      setIsInstalledPwa(getIsPwaInstalled());
      setIsPwaInfoTestEnabled(getIsPwaInfoTestEnabled());
    };

    updatePwaFlags();

    mediaStandalone.addEventListener?.('change', updatePwaFlags);
    mediaFullscreen.addEventListener?.('change', updatePwaFlags);
    mediaMinimalUi.addEventListener?.('change', updatePwaFlags);
    window.addEventListener('popstate', updatePwaFlags);

    return () => {
      mediaStandalone.removeEventListener?.('change', updatePwaFlags);
      mediaFullscreen.removeEventListener?.('change', updatePwaFlags);
      mediaMinimalUi.removeEventListener?.('change', updatePwaFlags);
      window.removeEventListener('popstate', updatePwaFlags);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const storageKey = getPwaInfoSeenStorageKey(userId);
      const seenValue = window.localStorage.getItem(storageKey);
      setIsPwaInfoSeen(seenValue === '1' || seenValue === 'true');
    } catch (_) {
      setIsPwaInfoSeen(false);
    }
  }, [userId]);
  
  
 
  useEffect(() => {
    if (combinedIsAndroidApp) {
      if (phrasesCount === 0) {
        requestMicrophonePermission();
      }
		if ((prevPhrasesCount.current < 10 || userInfo?.push_status === 0) && phrasesCount >= 10) {
		  requestPushPermission(userId).then((status) => {
			userInfo.push_status = status; // обновим локально после ответа
		  });
		}
    }
	  // if (platform === 'browser' && prevPhrasesCount.current < 10 && phrasesCount >= 10 && deferredPromptRef.current) {
		// setShowInstallPrompt(true);
	  // }	

    prevPhrasesCount.current = phrasesCount;
  }, [phrasesCount]);

  // меню 
  const [activeScreen, setActiveScreen] = useState('home');
  const [isLegacyHomeMode, setIsLegacyHomeMode] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('main');
  const [activedialogId, setActivedialogId] = useState(null);
  const [activelessonId, setActivelessonId] = useState(null);
  const [activeblockId, setActiveblockId] = useState(null);
  const [activeexamId, setActiveexamId] = useState(null);
  const [activePathStep, setActivePathStep] = useState(null);
  const normalizedUiLanguage = String(language || '').trim().toLowerCase() === 'ua'
    ? 'uk'
    : String(language || '').trim().toLowerCase();
  const isPwaInfoLanguageSupported = normalizedUiLanguage === 'ru' || normalizedUiLanguage === 'uk';
  const isBrowserPwaEligibleClient =
    platform === 'browser' &&
    !combinedIsAndroidApp &&
    !config?.isTelegramWebApp;
  const hasPwaInfoAgeGatePassed =
    Number.isFinite(registrationTimestamp) &&
    Date.now() - registrationTimestamp >= PWA_INFO_MIN_ACCOUNT_AGE_MS;
  const canShowPwaInfoTrigger =
    isPwaInfoLanguageSupported &&
    isBrowserPwaEligibleClient &&
    !isInstalledPwa &&
    !isLegacyHomeMode &&
    (hasPwaInfoAgeGatePassed || isPwaInfoTestEnabled) &&
    !isPwaInfoSeen &&
    !temp;
  
  //======== Реклама ==========================//
	const [promo, setPromo] = useState(null);

	const checkPromo = async (trigger, screenOverride) => {
	  try {
		if (!isLanguageLoaded) return;

		const screen = screenOverride ?? activeScreen;
		const result = await fetchData('/api/promo/check', 'POST', {
		  userId,
		  isTemp: temp,
		  language,
		  trigger,
		  screen
		});
		if (result?.promo?.id) {
		  setPromo(result.promo);
		} 
	  } catch (error) {
		console.error('❌ Error checking promo:', error);
	  }
	};

  const handlePwaInfoAcknowledge = () => {
    setIsPwaInfoSeen(true);
    setIsPwaInfoOpen(false);

    if (typeof window === 'undefined') return;

    try {
      const storageKey = getPwaInfoSeenStorageKey(userId);
      window.localStorage.setItem(storageKey, '1');
    } catch (_) {
      // ignore storage write issues
    }
  };
  
  //=== Статистика === 
  const isAudioTimeOver = audioTime <= 0; // если меньше 0 секунд, то закрывается всё! Отправляем в LessonScreen + TranslateBlock
  const is15SecondAudio = audioTime <= 15; // если меньше 15 секунд, то закрываются Диалоги. 
  const is30SecondAudio = audioTime <= 30; // если меньше 30 секунд, закрывается Игра и Экзамен

	const fetchAudioTime = async () => {
	  try {
		const result = await fetchData('/api/audio-time', 'POST', { userId, isTemp: temp });
		if (result.currentAudioTime !== undefined) {
		  setAudioTime(result.currentAudioTime);
		} 
	  } catch (error) {
		console.error('❌ Error fetching audio time:', error);
	  }
	};
	useEffect(() => {
	  if (userId && isLanguageLoaded) {
		fetchAudioTime();
	  }
	}, [activeScreen]);

	useEffect(() => {
	  if (activeScreen !== 'home') {
		setIsLegacyHomeMode(false);
	  }
	}, [activeScreen]);

  useEffect(() => {
    if (!canUseLegacyHomeMode) {
      setIsLegacyHomeMode(false);
    }
  }, [canUseLegacyHomeMode]);

  useEffect(() => {
    if (activeScreen !== 'home' || !canShowPwaInfoTrigger) {
      setIsPwaInfoOpen(false);
    }
  }, [activeScreen, canShowPwaInfoTrigger]);
 
  // Добавление очков с анимацией
  const addPoints = (amount) => {
    setPoints((prev) => prev + amount);
    setPointsDelta(amount);
    setTimeout(() => setPointsDelta(null), 5000);
  };

  // Добавление фразы
  const addPhrases = (amount) => {
    setPhrasesCount((prev) => prev + amount);
    setPhrasesDelta(amount);
    setTimeout(() => setPhrasesDelta(null), 5000);
  };  

  // Добавление времени аудио
  const addAudioTime = (seconds) => {
	setAudioTime((prev) => prev - seconds);
  };
  
	const moduleBack = (points, steps) => {
	  addPoints(points);
	  addPhrases(steps);
	  setActiveScreen('home');
	  checkPromo('module_complete');
	};  
  
	//Выбираем номер урока и номер блока. Функция использует данные из LessonList
	const handleLessonSelect = (lessonId, blockId) => {
	  setActivelessonId(lessonId);
	  setActiveblockId(blockId); // Добавь этот setState, если его ещё нет
	  setActiveScreen('lessonScreen');
	};
	
	const flagMap = {
	  de: deFlagImg,
	  en: enFlagImg,
	  ru: ruFlagImg,
	  uk: ukFlagImg,
	  // Добавляй по мере необходимости
	};
	
  //else if (key === 'igra') setActiveScreen('game');
  
  const renderContent = () => {
    switch (activeScreen) {
      case 'home':
		// тут надо менять и в MainMenu
        return (
          <PathHome
            userId={userId}
            language={language}
            targetLanguage={targetLanguage}
            vipLevel={vipLevel}
            phrasesCount={phrasesCount}
            selectedLevel={gameLevel}
            onLevelChange={setGameLevel}
            onGoToVip={() => setActiveScreen('vip')}
            onOpenLessons={() => setActiveScreen('pathLessons')}
            showPwaInfoTrigger={canShowPwaInfoTrigger}
            onOpenPwaInfo={() => setIsPwaInfoOpen(true)}
            legacyMode={isLegacyHomeMode}
            canToggleLegacyMode={canUseLegacyHomeMode}
            onLegacyModeChange={setIsLegacyHomeMode}
            onSelectLegacy={(key) => {
              if (key === 'lernen') setActiveScreen('lessons');
              else if (key === 'dialog') setActiveScreen('dialogs');
              else if (key === 'igra') setActiveScreen('game');
              else if (key === 'test') setActiveScreen('exams');
            }}
            onOpenStep={(step) => {
              setActivePathStep(step);
              if (step.step_type === 'dialog' || step.step_type === 'path_dialogs') {
                setActiveScreen('pathDialogs');
              } else if (step.step_type === 'exam' || step.step_type === 'path_exams') {
                setActiveScreen('pathExams');
              } else if (
                step.step_type === 'bundle_easy' ||
                step.step_type === 'bundle_medium' ||
                step.step_type === 'review_easy' ||
                step.step_type === 'review_medium' ||
                step.step_type === 'level_review_easy' ||
                step.step_type === 'level_review_medium'
              ) {
                setActiveScreen('pathCheck');
              } else if (step.step_type === 'practice') {
                setActiveScreen('pathPractice');
              } else if (step.step_type === 'easy' || step.step_type === 'medium') {
                setActiveScreen('pathCheck');
              } else {
                setActiveScreen('pathLearn');
              }
            }}
          />
        );
      case 'dialogs':
        return <DialogList onDialogSelect={(dialogId) => { setActivedialogId(dialogId); setActiveScreen('dialogScreen'); }} userId={userId} />;
      case 'dialogScreen':
            return is15SecondAudio ? <AccessDenied onBack={() => setActiveScreen('home')} activeScreen={activeScreen}  /> : (
			  <DialogScreen
                dialogId={activedialogId}
                onBack={moduleBack}
                userId={userId}
                onGoPremium={() => setActiveScreen('vip')}
              />
			);
      case 'lessons':
		return  <LessonList onLessonSelect={handleLessonSelect} userId={userId} onBack={() => setActiveScreen('lessons')} /> 		
      case 'lessonScreen':
        return (
          <LessonScreen
            lessonId={activelessonId}
            blockId={activeblockId}
            userId={userId}
            onExit={moduleBack}
            onGoPremium={() => setActiveScreen('vip')}
            audioDisabled={isAudioTimeOver}
          />
        );			
      case 'exams':
        return <ExamList onExamSelect={(examId) => { setActiveexamId(examId); setActiveScreen('examScreen'); }} userId={userId} />;
	  case 'examScreen': // || temp
		return (is30SecondAudio) ? <AccessDenied onBack={() => setActiveScreen('home')} activeScreen={activeScreen} />
			: ( <ExamScreen 
				  examId={activeexamId}
				  userId={userId}
				  onComplete={() => setActiveScreen('home')}
				  onExit={moduleBack}
                  onGoPremium={() => setActiveScreen('vip')}
				/>
			  );
      case 'game':
		return is30SecondAudio ? <AccessDenied onBack={() => setActiveScreen('home')} activeScreen={activeScreen}/> : (
		  <Game
            userId={userId}
            gameLevel={gameLevel}
            allPhrasesCount={phrasesCount}
            onExit={moduleBack}
            onGoPremium={() => setActiveScreen('vip')}
          />
		);		
	  case 'pathLearn':
		return (
		  <PathLearn
		    userId={userId}
		    step={activePathStep}
		    onBack={() => setActiveScreen('home')}
		  />
		);
	  case 'pathLessons':
		return (
		  <PathLessons
		    userId={userId}
		    audioDisabled={isAudioTimeOver}
		    onBack={() => setActiveScreen('home')}
		    onComplete={moduleBack}
		  />
		);
	  case 'pathCheck':
		return (
		  <PathCheckGame
		    userId={userId}
		    step={activePathStep}
            allPhrasesCount={phrasesCount}
		    onExit={moduleBack}
		    onBack={() => setActiveScreen('home')}
            onGoPremium={() => setActiveScreen('vip')}
		  />
		);
	  case 'pathPractice':
		return (
		  <PathPractice
		    userId={userId}
		    step={activePathStep}
		    onBack={() => setActiveScreen('home')}
		  />
		);
	  case 'pathDialogs':
		return (
		  <PathDialogs
		    userId={userId}
		    step={activePathStep}
		    onBack={() => setActiveScreen('home')}
		  />
		);
	  case 'pathExams':
		return (
		  <PathExams
		    userId={userId}
		    step={activePathStep}
		    onBack={() => setActiveScreen('home')}
		  />
		);
	  case 'referral':
        return <Referral userId={userId} platform={platform} onClick={() => setActiveScreen('vip')} />; // из Реферал можно напрямую переходить в Premium и наоборот
      case 'settings':
        return <Settings userId={userId} initialTab={settingsInitialTab} />;
      case 'vip':
        return <Premium userId={userId} premiumLevel={vipLevel} onClick={() => setActiveScreen('referral')} />; // из Реферал можно напрямую переходить в Premium и наоборот
      default:
        return <div>{`${SITE_CONFIG.brandName} - ${SITE_CONFIG.brandTagline}`}</div>;
    }
  };

  const isMobileBrowser = config.isMobile || config.isTelegramWebApp;
  const wrapperClass = combinedIsAndroidApp ? styles.androidWrapper
	: isMobileBrowser
	  ? styles.mobileWrapper
	  : styles.desktopWrapper;
	  
  const imageSrc = flagMap[targetLanguage?.toLowerCase()] || deFlagImg; // выбираем ассистента

  // Показываем состояние загрузки пока не определились с языком
  if (isLoading || !isLanguageLoaded) {
    return <div>Loading...</div>;
  }
      
	const AppContent = () => {
	  useAssistantHints(activeScreen, userId, language, targetLanguage, serverSettings, learnedWordsCount);
	  return (
		<ErrorProvider>
		 <ErrorBoundary userId={userId}>
		  <GlobalErrorBanner />
		   <div className={wrapperClass} >
			<div className={styles.app}>
			{!language || !targetLanguage ? (
				<WelcomeBlock userId={userId} onSettingsSaved={handleWelcomeSaved} />
			) : (
			  <>
			  {/*userId === "ace2d0b4-c2c6-48e8-9ade-87af6bfbee37" && <Test />*/}
			  <Header 
				audioTime={audioTime} 
				phrasesCount={phrasesCount}
				learnedWordsCount={learnedWordsCount}
				vip={vipLevel}
				onSettingsClick={() => {
				  setSettingsInitialTab('main');
				  setActiveScreen('settings');
				}}
				onVipClick={() => setActiveScreen('vip')}
				onRefClick={() => setActiveScreen('referral')}
				onBackClick={() => setActiveScreen('home')}
			  />
				{ activeScreen === 'home' ? (
				  <Logo
				    userId={userId}
				    phrasesCount={phrasesCount}
				    forceLegacyPdf={isLegacyHomeMode}
				    onGoToVip={() => setActiveScreen('vip')}
				    onOpenProfile={() => {
				      setSettingsInitialTab('profile');
				      setActiveScreen('settings');
				    }}
				    onOpenLanguageSettings={() => {
				      setSettingsInitialTab('main');
				      setActiveScreen('settings');
				    }}
				  />
				) : null}
				{!['referral', 'vip'].includes(activeScreen) && (
				  <>
					{activeScreen === 'examScreen' || activeScreen === 'pathExams' ? (
					  <Assistant imageSrc={imageSrc} isFixed={true} fixedPosition={{ x: 50, y: 100 }} />
					) : activeScreen === 'game' || activeScreen === 'pathCheck' ? (
					  <Assistant imageSrc={imageSrc} isFixed={true} fixedPosition={{ x: window.innerWidth / 2 - 50, y: window.innerHeight - 125 }}/>
					) : activeScreen === 'lessonScreen' ? (
					  <Assistant imageSrc={imageSrc} isFixed={false} fixedPosition={{ x: window.innerWidth - 100, y: 200 }} />
					): activeScreen === 'pathLearn' ? (
					  <Assistant imageSrc={imageSrc} isFixed={false} fixedPosition={{ x: window.innerWidth - 100, y: 50 }} />
					): activeScreen === 'home' ? (
					  <Assistant imageSrc={imageSrc} isFixed={true} isDraggable={true} fixedPosition={{ x: window.innerWidth - 100, y: window.innerHeight - 100 }} />
					):(
					  <Assistant imageSrc={imageSrc} />
					)}
				  </>
				)}
			    <div className={styles.contentBackground} data-screen={activeScreen}>{renderContent()}</div>
				{temp && <RegistrationButtons userId={userId} activeScreen={activeScreen}/>}
				{!temp && activeScreen === 'home' && <CharityButtons />}
			    <FlyingPoints amount={pointsDelta} />
			    <FlyingPhrases amount={phrasesDelta} />
				{userInfo?.showVip && <ShowVip language={language} />}

				<PwaInfoModal
				  open={isPwaInfoOpen}
				  onClose={() => setIsPwaInfoOpen(false)}
          onAcknowledge={handlePwaInfoAcknowledge}
				/>
				
				<PromoModal
				  promo={promo}
				  lang={language === 'ua' ? 'uk' : language}
				  onClose={() => setPromo(null)}
				  onConfirm={() => {
					const cta = promo?.cta;
					setPromo(null);
					if (cta?.type === 'screen') setActiveScreen(cta.value);
					else setActiveScreen('vip'); // fallback
				  }}
				/>				
			  </>
			)}
			</div>
		  </div>
		</ErrorBoundary>
	   </ErrorProvider>    
	  );
	};
	// выбор разных аудиопровайдеров в зависимости от isTelegramWebApp
	  return (
		<AppConfigProvider key={`${config.baseLanguage}-${config.targetLanguage}`} initialConfig={config}>
		  <AssistantProvider>
			{config.isTelegramWebApp ? (
			  <AudioTelegramRecorderProvider telegramWebApp={true} userId={userId}>
				<AppContent />
			  </AudioTelegramRecorderProvider>
			) : combinedIsAndroidApp ? (
			  <AndroidAudioRecorderProvider userId={userId} language={targetLanguage} source={activeScreen}>
				<AppContent />
			  </AndroidAudioRecorderProvider>
			) : (
			  <AudioRecorderProvider userId={userId} language={targetLanguage} source={activeScreen}>
				<AppContent />
			  </AudioRecorderProvider>
			)}
		  </AssistantProvider>
		</AppConfigProvider>
	  );
};

export default App;
/// images/flags/nen.png"
//<AppConfigProvider config={config}>
// для русского images/flags/ru.png" или images/flags/ru1.png"
// <WelcomeBlock userId={userId} onSettingsSaved={loadLanguage} />
//import TempUserReminder  from './components/TempUserReminder/TempUserReminder';
//{/*temp && activeScreen === 'home' && (<TempUserReminder baseLanguage={language} />)*/}
